import {
  useState,
  type ChangeEvent,
  type FC,
  useCallback,
  useRef,
} from 'react';
import { useDataProvider, useNotify, useTranslate } from 'ra-core';
import DataGrid, {
  type Column,
  type RenderEditCellProps,
} from 'react-data-grid';
import { UserRoundX } from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import { type AdminDataProvider } from '../../api/adminDataProvider';
import { useMutation } from '@tanstack/react-query';
import {
  type DisableUserResultRow,
  type DisableUserRow,
  type UserCsvValidationError,
  createDisableUserSchema,
} from './types';
import { saveSpreadsheet } from '../../utils/spreadsheet/save';
import { readSpreadsheet } from '../../utils/spreadsheet/read';
import { useCurrentUser } from '../../auth/currentUserContext';
import '../BulkOperation/dataGrid.css';

async function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export const BulkDisableUsersButton: FC = () => {
  const translate = useTranslate();
  const bulkDisableRef = useRef<HTMLInputElement>(null);
  const dataProvider = useDataProvider<AdminDataProvider>();
  const notify = useNotify();
  const { id: currentUserId } = useCurrentUser();

  const [csvData, setCsvData] = useState<DisableUserResultRow[]>([]);
  const [validationErrors, setValidationErrors] = useState<
    UserCsvValidationError[][]
  >([]);
  const [open, setOpen] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState({
    successCount: 0,
    errorCount: 0,
    total: 0,
    unprocessed: [] as DisableUserResultRow[],
  });

  const handleCellEdit = async (
    rowIdx: number,
    columnKey: keyof DisableUserRow,
    newValue: string
  ) => {
    const updatedRow = {
      ...csvData[rowIdx],
      [columnKey]: newValue,
    } as DisableUserResultRow;

    const rows = [...csvData];
    rows[rowIdx] = updatedRow;
    setCsvData(rows);

    const rowErrors = await validateRow(updatedRow);

    // eslint-disable-next-line @typescript-eslint/no-misused-spread -- we need to delete entries that have no errors later, so we use an object
    const newValidationErrors = { ...validationErrors };
    if (rowErrors.length > 0) {
      newValidationErrors[rowIdx] = rowErrors;
    } else {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete newValidationErrors[rowIdx];
    }

    setValidationErrors(newValidationErrors);
  };

  const autoFocusAndSelect = (input: HTMLInputElement | null) => {
    input?.focus();
    input?.select();
  };

  const TextEditor = <TRow, TSummaryRow>({
    row,
    column,
    onRowChange,
    onClose,
    rowIdx,
  }: RenderEditCellProps<TRow, TSummaryRow> & { rowIdx: number }) => {
    const [localValue, setLocalValue] = useState<string>(
      row[column.key as keyof TRow] as unknown as string
    );

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setLocalValue(event.target.value);
    };

    const handleBlur = async () => {
      onRowChange({ ...row, [column.key]: localValue });
      await handleCellEdit(
        rowIdx,
        column.key as keyof DisableUserRow,
        localValue
      );
      onClose(true, false);
    };

    const handleKeyDown = async (
      event: React.KeyboardEvent<HTMLInputElement>
    ) => {
      if (event.key === 'Enter') {
        onRowChange({ ...row, [column.key]: localValue });
        await handleCellEdit(
          rowIdx,
          column.key as keyof DisableUserRow,
          localValue
        );
        onClose(true, false);
      } else if (event.key === 'Escape') {
        onClose(false, false);
      }
    };

    return (
      <input
        className="rdg-text-editor rdg-cell"
        ref={autoFocusAndSelect}
        value={localValue}
        onChange={handleChange}
        onBlur={() => void handleBlur()}
        onKeyDown={(e) => void handleKeyDown(e)}
      />
    );
  };

  const columns: readonly Column<DisableUserResultRow>[] = [
    {
      key: 'rowNumber',
      name: '',
      width: 40,
      renderCell: (props) => <div>{props.rowIdx + 1}</div>, // rowIdx is 0-based, so add 1 for display
    },
    {
      key: 'id',
      name: 'ID',
      renderEditCell: TextEditor,
      renderCell: ({ row, rowIdx }) => renderCellWithTooltip(row, 'id', rowIdx),
    },
    {
      key: 'internalId',
      name: 'Internal ID',
      renderEditCell: TextEditor,
      renderCell: ({ row, rowIdx }) =>
        renderCellWithTooltip(row, 'internalId', rowIdx),
    },
    {
      key: 'firstName',
      name: 'First name',
      editable: false,
    },
    {
      key: 'lastName',
      name: 'Last name',
      editable: false,
    },
  ];

  const onClickBulkDisable = useCallback(() => {
    if (!bulkDisableRef.current) return;
    bulkDisableRef.current.value = '';
    bulkDisableRef.current.click();
  }, []);

  const validateRow = async (user: DisableUserRow) => {
    const schema = createDisableUserSchema(dataProvider);
    const validationResult = await schema.safeParseAsync(user);

    const errors =
      validationResult.error?.errors.map((error) => ({
        property: error.path.toString(),
        message: error.message,
      })) ?? [];

    if (!errors.length && validationResult.data) {
      user.id = validationResult.data.id ?? '';
      user.internalId = validationResult.data.internalId ?? '';
      user.firstName = validationResult.data.firstName ?? '';
      user.lastName = validationResult.data.lastName ?? '';
      if (Number(user.id) === currentUserId) {
        const message = translate(
          'moonstar.user.disable.selfDisableExplanation'
        );
        errors.push({ property: 'id', message });
        if (user.internalId) errors.push({ property: 'internalId', message });
      }
    } else {
      user.firstName = '';
      user.lastName = '';
    }

    return errors;
  };

  const validateAllRows = async (jsonData: DisableUserRow[]) => {
    const validationPromises = jsonData.map(async (user, index) => {
      const validationErrors = await validateRow(user);

      return validationErrors.length > 0
        ? { index, errors: validationErrors }
        : null;
    });

    const validationResults = await Promise.all(validationPromises);

    const errors: UserCsvValidationError[][] = [];
    validationResults.forEach((result) => {
      if (result) {
        errors[result.index] = result.errors;
      }
    });

    return errors;
  };

  const bulkDisableUsers = useMutation({
    mutationFn: async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      setCsvData([]);
      setValidationErrors([]);
      setShowSummary(false);
      setProcessing(false);
      setProgress({
        successCount: 0,
        errorCount: 0,
        total: 0,
        unprocessed: [],
      });

      const rows = await readSpreadsheet(file).then((r) =>
        r.map<DisableUserResultRow>((row) => ({
          id: row.id || '',
          internalId: row.internalId || '',
          firstName: '',
          lastName: '',
          status: 'pending',
        }))
      );

      const errors = await validateAllRows(rows);
      setValidationErrors(errors);
      setCsvData(rows);
      setOpen(true);
    },
    onError: () => {
      notify(translate('moonstar.bulk_disable.error'), {
        type: 'error',
        autoHideDuration: 5000,
      });
    },
  });

  const handleUpload = async () => {
    setProcessing(true);
    const rows = csvData.map((row) => ({ ...row }));
    let errors: UserCsvValidationError[][];
    try {
      errors = await validateAllRows(rows);
    } catch (error) {
      console.error(error);
      setProcessing(false);
      notify(translate('moonstar.bulk_disable.error'), {
        type: 'error',
        autoHideDuration: 5000,
      });
      return;
    }
    setCsvData(rows);
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) {
      setProcessing(false);
      return;
    }

    setShowSummary(true);
    let successCount = 0;
    let errorCount = 0;

    const unprocessedRows = progress.unprocessed;

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i]!;

      row.status = 'processing';
      setCsvData([...rows]);
      try {
        if (Number(row.id) === currentUserId) {
          throw new Error(
            translate('moonstar.user.disable.selfDisableExplanation')
          );
        }
        const response = await dataProvider.setUserEnabled(row.id, false);
        if (response.status == 200 || response.status == 204) {
          successCount++;
          row.status = 'success';
        } else {
          errorCount++;
          row.status = 'error';
          unprocessedRows.push({
            ...row,
            errorMessage: response.json,
          } as DisableUserResultRow);
        }
      } catch (error) {
        const isSelfDisable = Number(row.id) === currentUserId;
        if (!isSelfDisable) console.error(error, row);
        errorCount++;
        row.status = 'error';
        unprocessedRows.push({
          ...rows[i],
          errorMessage: isSelfDisable
            ? translate('moonstar.user.disable.selfDisableExplanation')
            : 'Unexpected error. Please reach out to Moonstar Support.',
        } as DisableUserResultRow);
      }
      setCsvData([...rows]);
      setProgress({
        successCount,
        errorCount,
        total: csvData.length,
        unprocessed: unprocessedRows,
      });
      // due too clerk's 20requests/10s limit
      await sleep(500);
    }

    setProcessing(false);
    if (errorCount === 0) {
      notify(translate('moonstar.bulk_disable.success'), { type: 'success' });
    } else {
      notify(
        translate('moonstar.bulk_disable.partial_success', {
          total_failed: errorCount,
        }),
        { type: 'error' }
      );
    }
  };

  const handleExport = async () => {
    await saveSpreadsheet<Record<keyof DisableUserResultRow, string>>({
      format: 'csv',
      data: progress.unprocessed,
      basename: 'bulk-disable-errors',
    });
  };

  const renderCellWithTooltip = (
    row: DisableUserRow,
    columnKey: keyof DisableUserRow,
    rowIdx: number
  ) => {
    const errors = validationErrors[rowIdx];
    const errorMessage = errors?.find(
      (error) => error.property === columnKey
    )?.message;

    return errorMessage ? (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger
            render={
              <div className="rdg-cell-error">{row[columnKey] || ''}</div>
            }
          />
          <TooltipContent>{errorMessage}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ) : (
      <div>{row[columnKey] || ''}</div>
    );
  };

  return (
    <>
      <Button
        type="button"
        onClick={onClickBulkDisable}
        disabled={bulkDisableUsers.isPending}
      >
        <UserRoundX />
        {translate('moonstar.bulk_disable.label')}
      </Button>
      <input
        ref={bulkDisableRef}
        type="file"
        style={{ display: 'none' }}
        onChange={bulkDisableUsers.mutate}
        accept=".csv,.tsv,.xlsx,.xls"
      />

      <Dialog
        open={open}
        onOpenChange={(nextOpen) => {
          if (!nextOpen && !processing) setOpen(false);
        }}
      >
        <DialogContent className="w-[calc(100%-2rem)] sm:max-w-6xl">
          <DialogHeader>
            <DialogTitle>
              {translate('moonstar.bulk_disable.label')}
            </DialogTitle>
          </DialogHeader>
          <div className="rdg-wrapper">
            <DataGrid
              columns={columns}
              rows={csvData}
              onRowsChange={setCsvData}
              defaultColumnOptions={{ resizable: true }}
              rowClass={(row) => {
                switch (row.status) {
                  case 'processing':
                    return 'row-processing';
                  case 'success':
                    return 'row-success';
                  case 'error':
                    return 'rdg-row-error';
                  default:
                    return '';
                }
              }}
            />
          </div>
          <div className="progress-summary">
            {showSummary ? (
              processing ? (
                <p>
                  Processing: {progress.successCount + progress.errorCount}/
                  {progress.total}
                </p>
              ) : (
                <p>
                  Disabling {progress.successCount} users, {progress.errorCount}{' '}
                  errors
                </p>
              )
            ) : null}
          </div>
          <DialogFooter>
            <Button
              onClick={() => void handleExport()}
              disabled={processing || progress.unprocessed.length === 0}
            >
              Export unprocessed
            </Button>
            <Button
              onClick={() => void handleUpload()}
              disabled={
                processing ||
                showSummary ||
                csvData.length === 0 ||
                Object.keys(validationErrors).length > 0
              }
            >
              Proceed
            </Button>
            <Button
              onClick={() => setOpen(false)}
              variant="outline"
              disabled={processing}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
